import { CSSProperties, Fragment, useEffect, useState } from 'react'

import type { ElementRects } from '@floating-ui/core'
import { flip, offset, shift, size, useFloating } from '@floating-ui/react-dom'
import { Transition } from '@headlessui/react'
import { clsx } from 'clsx'
import rafSchd from 'raf-schd'

import { Portal } from '../../Portal/index.js'
import { useFloatingContext } from './FloatingContext.js'
import { useUpdateOnScroll } from './useUpdateOnScroll.js'

export const FloatingWrapper = ({
  open,
  sameWidth = false,
  skipTransition = false,
  children,
}) => {
  const { placement, triggerRef } = useFloatingContext()

  const [referenceSizeData, setReferenceSizeData] = useState<
    ElementRects['reference'] | null
  >(null)
  const middleware = [
    offset(4),
    shift(),
    flip({
      fallbackStrategy: 'initialPlacement',
    }),
  ]
  if (sameWidth) {
    middleware.push(
      size({
        apply({ rects }) {
          setReferenceSizeData(rects.reference)
        },
      }),
    )
  }

  const { x, y, reference, floating, strategy, update } = useFloating({
    placement,
    middleware,
  })

  useEffect(() => {
    reference(triggerRef.current)
  }, [reference, triggerRef])
  useUpdateOnScroll(triggerRef, update, 'scroll')
  useUpdateOnScroll(triggerRef, update, 'resize')

  useEffect(() => {
    const schedule = rafSchd(update)
    if (open) {
      schedule()
    }
    return () => schedule?.cancel()
  }, [update, open, children])

  if (!open) {
    return null
  }

  const style: CSSProperties = {
    position: strategy,
    top: '0',
    left: '0',
    transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
  }
  if (sameWidth) {
    style.width = referenceSizeData?.width ?? ''
    style.maxWidth = referenceSizeData?.width ?? ''
  }

  // downshift does not work will with transition
  const content = skipTransition ? (
    children
  ) : (
    <Transition
      show={open}
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      {children}
    </Transition>
  )

  return (
    <Portal>
      <div
        ref={floating}
        style={style}
        className={clsx(!skipTransition && x === null && 'invisible')}
      >
        {content}
      </div>
    </Portal>
  )
}
